import {LinearGradient} from 'expo-linear-gradient';
import React, {useEffect, useImperativeHandle, forwardRef, useRef, useState} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator
} from 'react-native';
import CommonStyles from '../../../style/commonStyles';
import i18n from 'i18n-js';
import {useSelector} from 'react-redux';
import {
  saveAwaitingWindowPreference,
  loadAwaitingWindowPreference
} from '../../../utils/awaitingWindowPreferences';

export const AwaitingWindowContent = forwardRef(({setModalVisible, onTooltipChange}, ref) => {
  const userInfo = useSelector(state => state.user.userInfo);
  const [days, setDays] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    closeTooltip: () => {
      setShowTooltip(false);
      onTooltipChange && onTooltipChange(false);
    }
  }));

  useEffect(() => {
    const savedDays = loadAwaitingWindowPreference(userInfo);
    setDays(savedDays);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSave = () => {
    const daysNum = parseInt(days, 10);
    if (isNaN(daysNum) || daysNum <= 0) {
      setError(i18n.t('awaitingWindow.invalidDaysError'));
      return;
    }

    setIsLoading(true);
    const success = saveAwaitingWindowPreference(userInfo, days);

    if (success) {
      setTimeout(() => {
        setIsLoading(false);
        setError('');
        setModalVisible(false);
      }, 300);
    } else {
      setIsLoading(false);
      setError(i18n.t('awaitingWindow.saveError'));
    }
  };

  const dismissTooltip = () => {
    if (showTooltip) {
      setShowTooltip(false);
      onTooltipChange && onTooltipChange(false);
    }
  };

  return (
    <Pressable onPress={dismissTooltip}>
      <LinearGradient
        colors={['#27374B', '#526B82']}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={styles.container}>
        <View style={styles.subContainer}>
        <View style={styles.headerContainer}>
          <Text style={styles.headerText}>
            {i18n.t('awaitingWindow.title')}
          </Text>
        </View>

        <View style={styles.inputRow}>
          <View style={styles.inputContainer}>
            <TextInput
              ref={inputRef}
              value={days}
              onChangeText={text => {
                setError('');
                setDays(text);
              }}
              placeholder="14"
              placeholderTextColor={'#898989'}
              style={styles.input}
              keyboardType="numeric"
            />
          </View>
          <Text style={styles.daysLabel}>{i18n.t('awaitingWindow.daysLabel')}</Text>
          <View style={styles.infoIconWrapper}>
            <TouchableOpacity
              style={styles.infoIconContainer}
              onPress={() => {
                const next = !showTooltip;
                setShowTooltip(next);
                onTooltipChange && onTooltipChange(next);
              }}
            >
              <i className="icon-info-circled" style={{fontSize: 20, color: '#ffff'}}></i>
            </TouchableOpacity>
            {showTooltip && (
              <LinearGradient
                locations={[0, 1]}
                colors={['#8a9daf', '#d7e1e9']}
                style={styles.tooltipContainer}>
                <Text style={styles.tooltipText}>
                  {i18n.t('awaitingWindow.tooltip')}
                </Text>
                <View style={styles.tooltipArrow} />
              </LinearGradient>
            )}
          </View>
        </View>

        {error.length > 0 && <Text style={styles.errorText}>{error}</Text>}

        <TouchableOpacity
          testID="saveButton"
          onPress={handleSave}
          style={[
            CommonStyles.saveButton,
            styles.saveChangeButton,
            {marginTop: 30}
          ]}
          disabled={isLoading}>
          {isLoading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={CommonStyles.saveButtonText}>
              {i18n.t('awaitingWindow.saveButton')}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </LinearGradient>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#546D85',
    minWidth: 350,
    padding: 10,
    borderRadius: 5,
    alignSelf: 'flex-start',
    overflow: 'visible'
  },
  subContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10%',
    justifyContent: 'center',
    overflow: 'visible'
  },
  headerContainer: {
    marginBottom: 20,
    alignItems: 'center'
  },
  headerText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 15,
    overflow: 'visible'
  },
  inputContainer: {
    backgroundColor: 'white',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 5
  },
  input: {
    width: 45,
    fontSize: 16,
    padding: 5,
    textAlign: 'center',
    color: '#000'
  },
  daysLabel: {
    fontSize: 16,
    marginLeft: 10,
    marginRight: 10,
    color: '#fff'
  },
  infoIconWrapper: {
    position: 'relative',
    overflow: 'visible'
  },
  infoIconContainer: {
    padding: 5,
    justifyContent: 'center',
    alignItems: 'center'
  },
  tooltipContainer: {
    position: 'absolute',
    bottom: 35,
    left: -10,
    minWidth: 300,
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    zIndex: 999
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -10,
    left: 15,
    width: 0,
    height: 0,
    borderLeftWidth: 10,
    borderLeftColor: 'transparent',
    borderRightWidth: 10,
    borderRightColor: 'transparent',
    borderTopWidth: 10,
    borderTopColor: '#d7e1e9'
  },
  tooltipText: {
    color: '#000',
    fontSize: 12,
    lineHeight: 16,
    fontFamily: 'Poppins_300Light'
  },
  saveChangeButton: {
    borderWidth: 2,
    shadowColor: '#E853FA',
    shadowOffset: {width: 0, height: 0.5},
    shadowRadius: 8,
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingVertical: 5,
    paddingHorizontal: 7
  },
  errorText: {
    color: '#FF6B6B',
    marginTop: 10,
    textAlign: 'center',
    fontSize: 14
  }
});
